import { useState, useEffect } from 'react';
import { VStack, Button, Text, Input } from '@chakra-ui/react';
import type { ValidationRule } from '@/lib/sdk/workflows/types';

interface DateInputTaskProps {
  description: string;
  validation: ValidationRule;
  onSubmit: (value: string) => void;
  isLoading?: boolean;
  // Controlled mode props
  controlled?: boolean;
  onValueChange?: (value: string) => void;
  initialValue?: string;
}

export function DateInputTask({
  description,
  validation,
  onSubmit,
  isLoading = false,
  controlled = false,
  onValueChange,
  initialValue = '',
}: DateInputTaskProps) {
  const [date, setDate] = useState(initialValue);
  const [error, setError] = useState('');

  // Update date when initialValue changes (for controlled mode)
  useEffect(() => {
    if (initialValue !== undefined) {
      setDate(initialValue);
    }
  }, [initialValue]);

  const handleChange = (value: string) => {
    setDate(value);
    setError('');
    if (controlled && onValueChange) {
      onValueChange(value);
    }
  };

  const handleSubmit = () => {
    if (validation.required && !date) {
      setError('This field is required');
      return;
    }

    setError('');
    // Convert YYYY-MM-DD to DDMMYYYY format
    const [year, month, day] = date.split('-');
    const formattedDate = `${day}${month}${year}`;
    onSubmit(formattedDate);
  };

  return (
    <VStack spacing={4} align="stretch">
      {!controlled && (
        <Text fontSize="lg" fontWeight="medium" color="text-primary">
          {description}
        </Text>
      )}

      <Input
        type="date"
        value={date}
        onChange={(e) => handleChange(e.target.value)}
        max={validation.max as string}
        bg="bg-surface"
        borderColor="border-default"
        _hover={{ borderColor: 'border-hover' }}
        _focus={{
          borderColor: 'brand.500',
          boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
        }}
      />

      {error && (
        <Text fontSize="sm" color="red.500">
          {error}
        </Text>
      )}

      {!controlled && (
        <Button
          colorScheme="brand"
          onClick={handleSubmit}
          isLoading={isLoading}
          isDisabled={validation.required && !date}
        >
          Submit
        </Button>
      )}
    </VStack>
  );
}